import { create } from "zustand"
import { immer } from "zustand/middleware/immer"
import type { NormalizedPost } from "../types"

interface PostStoreState {
  entities: Record<string, NormalizedPost>
}

interface PostStoreActions {
  upsertPost: (post: NormalizedPost) => void
  upsertPosts: (posts: NormalizedPost[]) => void
  updatePost: (id: string, patch: Partial<NormalizedPost>) => void
  removePost: (id: string) => void
  clearPosts: () => void
}

// Track posts whose interaction state was mutated by frontend actions
// so upsertPost doesn't overwrite them with stale API data
const interactionMutated = new Set<string>()

/** Mark a post's interaction state as frontend-mutated (called after like/favorite toggle) */
export function markInteractionMutated(postId: string) {
  interactionMutated.add(postId)
}

export const usePostStore = create<PostStoreState & PostStoreActions>()(
  immer((set) => ({
    entities: {},
    upsertPost: (post) => set((state) => {
      const existing = state.entities[post.id]
      if (existing && interactionMutated.has(post.id)) {
        state.entities[post.id] = { ...post, isLiked: existing.isLiked, isFavorited: existing.isFavorited }
      } else {
        state.entities[post.id] = post
      }
    }),
    upsertPosts: (posts) => set((state) => { for (const post of posts) {
      const existing = state.entities[post.id]
      if (existing && interactionMutated.has(post.id)) {
        state.entities[post.id] = { ...post, isLiked: existing.isLiked, isFavorited: existing.isFavorited }
      } else {
        state.entities[post.id] = post
      }
    } }),
    updatePost: (id, patch) => set((state) => { const existing = state.entities[id]; if (existing) { Object.assign(existing, patch) } }),
    removePost: (id) => set((state) => { delete state.entities[id] }),
    clearPosts: () => set((state) => { state.entities = {}; interactionMutated.clear() }),
  }))
)
